import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type Attachment = { objectKey: string; fileName: string; mediaType: string; byteSize: number };
type ReviewEvidence = {
  evidenceId: string;
  title: string;
  status: string;
  narrative?: string | null;
  externalUrl?: string | null;
  attachments: Attachment[];
};
type ReviewItem = {
  experienceId: string;
  learnerId: string;
  displayName: string;
  gradeLevel?: string | null;
  classLabel?: string | null;
  title: string;
  experienceType: string;
  organizationOrPlace?: string | null;
  startedOn: string;
  endedOn?: string | null;
  description?: string | null;
  role?: string | null;
  status: string;
  reviewFeedback?: string | null;
  version: number;
  updatedAt: string;
  reflection: { status: string; content: Record<string, string> };
  evidence: ReviewEvidence[];
};
type Queue = {
  counts: { submitted: number; needsRevision: number; verified: number };
  items: ReviewItem[];
};

const REFLECTION_LABELS: Array<[string, string]> = [
  ["whatIDid", "내가 한 일"], ["role", "나의 역할"], ["memorableMoment", "기억에 남는 순간"],
  ["challenge", "어려웠던 점"], ["action", "내가 한 선택"], ["learning", "배운 점"],
  ["strength", "나의 강점"], ["nextTime", "다음에 다시 한다면"]
];

function statusLabel(value: string) {
  const labels: Record<string, string> = { SUBMITTED: "확인 요청", NEEDS_REVISION: "보완 요청", REJECTED: "재검토 필요", VERIFIED: "교사 확인 완료" };
  return labels[value] || value;
}

export function CenterPersonalExperienceReview({ client, schoolId }: { client: PlatformClient; schoolId: string }) {
  const [queue, setQueue] = useState<Queue | null>(null);
  const [selectedId, setSelectedId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let active = true;
    setError("");
    Promise.resolve(client.rpc("center_experience_review_queue", { p_school_id: schoolId })).then(({ data, error: rpcError }) => {
      if (!active) return;
      if (rpcError) { setError(rpcError.message); setQueue(null); return; }
      const result = data as Queue;
      setQueue(result);
      setSelectedId((current) => result.items.some((item) => item.experienceId === current)
        ? current
        : result.items.find((item) => item.status === "SUBMITTED")?.experienceId || result.items[0]?.experienceId || "");
    }).catch((cause) => { if (active) setError(cause instanceof Error ? cause.message : "학생 경험을 불러오지 못했습니다."); });
    return () => { active = false; };
  }, [client, schoolId, refresh]);

  const selected = useMemo(() => queue?.items.find((item) => item.experienceId === selectedId) || null, [queue, selectedId]);
  useEffect(() => { setReason(""); }, [selectedId]);

  async function review(status: "VERIFIED" | "NEEDS_REVISION" | "REJECTED") {
    if (!selected) return;
    if (status !== "VERIFIED" && !reason.trim()) { setError("보완 요청 또는 반려 사유를 적어주세요."); return; }
    setBusy(true); setError("");
    const { error: rpcError } = await client.rpc("center_transition_personal_experience", {
      p_school_id: schoolId,
      p_experience_id: selected.experienceId,
      p_expected_version: selected.version,
      p_to_status: status,
      p_reason: reason.trim() || null
    });
    setBusy(false);
    if (rpcError) { setError(rpcError.message); return; }
    setRefresh((value) => value + 1);
  }

  async function openAttachment(attachment: Attachment) {
    const { data, error: storageError } = await client.storage.from("rs-career-artifacts").createSignedUrl(attachment.objectKey, 120);
    if (storageError || !data?.signedUrl) { setError(storageError?.message || "증빙을 열지 못했습니다."); return; }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  }

  return (
    <section className="experience-review-queue" aria-labelledby="personal-experience-review-title">
      <div className="experience-review-queue-header">
        <div><p className="eyebrow">학생 자기기록 검토</p><h2 id="personal-experience-review-title">학생이 제출한 경험</h2><p className="muted">학생이 작성한 기록과 증빙을 확인하고 상태를 처리합니다.</p></div>
        {queue && <div className="experience-review-counts"><span>확인 요청 {queue.counts.submitted}</span><span>보완 요청 {queue.counts.needsRevision}</span><span>확인 완료 {queue.counts.verified}</span></div>}
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {!queue ? <p className="muted">학생 경험 검토함을 불러오는 중…</p> : !queue.items.length ? <p className="case-empty">제출된 개인 경험이 없습니다.</p> : (
        <div className="experience-review-layout">
          <div className="experience-review-list" aria-label="제출된 학생 경험 목록">
            {queue.items.map((item) => <button key={item.experienceId} type="button" className={selectedId === item.experienceId ? "active" : ""} onClick={() => setSelectedId(item.experienceId)}>
              <strong>{item.title}</strong><span>{item.displayName} · {item.gradeLevel || "학년 미상"}{item.classLabel ? ` ${item.classLabel}반` : ""}</span><small>{statusLabel(item.status)} · {new Date(item.startedOn).toLocaleDateString("ko-KR")}</small>
            </button>)}
          </div>
          {selected && <div className="experience-review-detail">
            <header><p className="eyebrow">{statusLabel(selected.status)}</p><h3>{selected.title}</h3><p>{selected.displayName} · {selected.organizationOrPlace || "장소 미입력"} · {new Date(selected.startedOn).toLocaleDateString("ko-KR")}</p></header>
            <div><strong>활동 설명</strong><p>{selected.description || "학생이 활동 설명을 작성하지 않았습니다."}</p></div>
            <div><strong>학생이 맡은 역할</strong><p>{selected.role || "기록 없음"}</p></div>
            <section><strong>학생 성찰 · {selected.reflection.status === "COMPLETED" ? "기록 완료" : "작성 중"}</strong>
              {REFLECTION_LABELS.map(([key, label]) => selected.reflection.content?.[key]?.trim() ? <p key={key}><b>{label}:</b> {selected.reflection.content[key]}</p> : null)}
            </section>
            <section className="experience-review-evidence"><strong>증빙 {selected.evidence.length}개</strong>
              {selected.evidence.map((evidence) => <article key={evidence.evidenceId}><b>{evidence.title} · {statusLabel(evidence.status)}</b>{evidence.narrative && <p>{evidence.narrative}</p>}{evidence.externalUrl && <a href={evidence.externalUrl} target="_blank" rel="noreferrer">외부 링크 열기 ↗</a>}{evidence.attachments.map((attachment) => <button key={attachment.objectKey} type="button" className="evidence-open-link" onClick={() => void openAttachment(attachment)}>{attachment.fileName} · {Math.ceil(attachment.byteSize / 1024)}KB · 열기</button>)}</article>)}
              {!selected.evidence.length && <p className="muted">첨부한 증빙이 없습니다.</p>}
            </section>
            {selected.status === "SUBMITTED" && <div className="experience-review-action"><label htmlFor="experience-review-reason">검토 메모 또는 보완 안내</label><textarea id="experience-review-reason" value={reason} onChange={(event) => setReason(event.target.value)} maxLength={1200} placeholder="보완 요청이나 반려 시 학생에게 전달할 내용을 적어주세요."/><div>
              <button className="secondary" type="button" disabled={busy || !reason.trim()} onClick={() => void review("NEEDS_REVISION")}>보완 요청</button>
              <button className="secondary danger" type="button" disabled={busy || !reason.trim()} onClick={() => void review("REJECTED")}>반려</button>
              <button className="primary" type="button" disabled={busy} onClick={() => void review("VERIFIED")}>교사 확인</button>
            </div></div>}
            {selected.reviewFeedback && selected.status !== "SUBMITTED" && <p className="muted">이전 검토 안내: {selected.reviewFeedback}</p>}
          </div>}
        </div>
      )}
    </section>
  );
}
